import { Component, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { Router, ActivatedRoute } from '@angular/router';
import { GoogleAnalyticsService } from 'ngx-google-analytics';
import { GenerateCvService } from 'src/app/pages/generate resume/generate-cv-steps/services/generate-cv/generate-cv.service';
import { PaymentService } from 'src/app/services/payment/payment.service';
import { SafeUrl } from '@angular/platform-browser';
import { CvDbService } from 'src/app/services/cv-db/cv-db.service';
import { environment } from 'src/environments/environment';
import { ImageService } from 'src/app/services/image/image.service';

@Component({
  selector: 'app-review-resume',
  templateUrl: './review-resume.component.html',
  styleUrls: ['./review-resume.component.css'],
})
export class ReviewResumeComponent implements OnInit {
  imageUrl?: SafeUrl;
  serviceSystemId: string = '';
  isPdfPurchased: boolean = false;
  isWordPurchased: boolean = false;
  pdfPath: string = '';
  wordPath: string = '';
  loadingPurchaseStatus: boolean = true;
  userInfo?: any = JSON.parse(localStorage.getItem('userInfo') || '{}');
  cvUrlImage: string = '';
  editMode: boolean = false;
  mainURL = 'assets/cvs/';
  imagePath = environment.apiUrl + this.mainURL;

  constructor(
    private translate: TranslateService,
    private fb: FormBuilder,
    private generateCvService: GenerateCvService,
    private paymentService: PaymentService,
    private router: Router,
    private route: ActivatedRoute,
    private gtmService: GoogleTagManagerService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private cvDbService: CvDbService,
    private imageService: ImageService
  ) {}

  async ngOnInit() {
    this.route.queryParams.subscribe((params) => {
      this.serviceSystemId =
        params['serviceSystemId'] ||
        localStorage.getItem('serviceSystemId') ||
        '';

      if (this.serviceSystemId) {
        this.loadCvDataFromApi();
      }
    });

    this.generateCvService.data$.subscribe((data) => {
      if (!data) return;

      const serviceId = data['serviceSystemId'];

      if (serviceId) {
        this.serviceSystemId = serviceId;
        localStorage.setItem('serviceSystemId', serviceId);
      }

      this.cvDbService.savePdf(data);

      if (this.serviceSystemId) {
        this.loadPurchaseStatus();
      }
    });
  }

  loadCvDataFromApi() {
    if (!this.serviceSystemId) return;

    this.loadingPurchaseStatus = true;

    this.paymentService
      .checkIsGeneratedCVPurchased(this.serviceSystemId)
      .subscribe({
        next: (res: any) => {
          this.isPdfPurchased = res.pdfpaid || false;
          this.isWordPurchased = res.wordPaid || false;
          this.pdfPath = res.generatedPDFCVpath || '';
          this.wordPath = res.generatedWordCVpath || '';

          const imagePath = res.generatedImageCVpath || null;

          if (imagePath) {
            this.loadCvImage(imagePath);
          } else {
            console.warn('⚠️ No image path in response');
            this.loadingPurchaseStatus = false;
          }
        },
        error: (err) => {
          console.error('❌ Error loading CV data:', err);
          this.loadingPurchaseStatus = false;
        },
      });
  }

  loadCvImage(imagePath: string) {
    if (!imagePath) {
      console.warn('⚠️ No image path provided');
      this.loadingPurchaseStatus = false;
      return;
    }

    // Set placeholder first
    this.cvUrlImage = 'assets/img/placeholder.png';

    // Build full URL
    const fullImageUrl = `${this.imagePath}${imagePath}`;

    this.imageService.getSecureImage(fullImageUrl).subscribe({
      next: (blobUrl) => {
        this.cvUrlImage = blobUrl;
        this.loadingPurchaseStatus = false;
      },
      error: (err) => {
        this.cvUrlImage = 'assets/img/error-placeholder.png';
        this.loadingPurchaseStatus = false;
      },
    });
  }

  loadPurchaseStatus() {
    if (!this.serviceSystemId) return;

    this.paymentService
      .checkIsGeneratedCVPurchased(this.serviceSystemId)
      .subscribe({
        next: (res: any) => {
          this.isPdfPurchased = res.pdfpaid || false;
          this.isWordPurchased = res.wordPaid || false;
          this.loadingPurchaseStatus = false;
        },
        error: (err) => {
          console.error('❌ Error checking purchase status:', err);
          this.loadingPurchaseStatus = false;
        },
      });
  }

  downloadFile(path: string) {
    if (!path) {
      console.error('❌ No file path provided');
      return;
    }

    const fullUrl = `${this.imagePath}${path}`;
    const fileName = path.split('/').pop() || 'download';

    this.imageService.downloadSecureFile(fullUrl, fileName).subscribe({
      next: (blob) => {
        // Create download link
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up blob URL
        setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
      },
      error: (err) => {
        console.error('❌ Error downloading file:', err);
        alert('Failed to download file. Please try again.');
      },
    });
  }

  onPrevious() {
    this.router.navigate(['/generatResumeSteps'], { queryParams: { step: 0 } });
  }

  goToPayment(productKey: string) {
    this.router.navigate(['/payment'], {
      state: {
        productKey: productKey,
        serviceSystemId: this.serviceSystemId,
      },
      queryParams: {
        productKey: productKey,
        serviceSystemId: this.serviceSystemId,
      },
    });
  }

  sendGoogleEvent(name: string) {
    this.googleAnalyticsService.event(name);
    this.gtmService.pushTag({
      event: 'customEvent',
      category: 'button',
      action: 'click',
      label: name,
    });
  }
}
